import { TerciarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Table, {
  Cell,
  Row,
  TitleCell,
  TitleRow
} from 'components/ReusableComponents/Table'
import BookingHistory from 'features/BookingDetails/BookingHistory'
import ReservationCompareDialog from 'features/MonthlyOTAConversions/OTABookingsList/ReservationCompareDialog'
import SelectBarChart from 'features/MonthlyOTAConversions/OTABookingsList/SelectBarChart'
import moment from 'moment'
import { useEffect, useState } from 'react'
import bookingsApi from 'services/api/bookings'
import { formatPrice } from 'utils/common'

import styles from './OTABookingsList.module.scss'

const monthNamesToNumbers = {
  Jan: 1,
  Feb: 2,
  Mar: 3,
  Apr: 4,
  May: 5,
  Jun: 6,
  Jul: 7,
  Aug: 8,
  Sep: 9,
  Oct: 10,
  Nov: 11,
  Dec: 12
}

function getDateRange(date1, date2) {
  if (moment(date1).format('MMM') === moment(date2).format('MMM')) {
    return `${moment(date1).format('MMM')} ${moment(date1).format('D')} - ${moment(
      date2
    ).format('D')}`
  } else {
    return `${moment(date1).format('MMM D')} - ${moment(date2).format('MMM D')}`
  }
}

const OTABookingsList = ({
  month,
  year,
  kind,
  parentChain,
  cityToFilter,
  groupToFilter,
  stateToFilter,
  isOtaOnly
}) => {
  const [sortIndex, setSortIndex] = useState(2)
  const [sortAscending, setSortAscending] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const [isLoading, setIsLoading] = useState(false)
  const [isErred, setIsErred] = useState(false)
  const [hotels, setHotels] = useState()

  const [activeBookingId, setActiveBookingId] = useState()
  const [activePriorBookingId, setActivePriorBookingId] = useState()
  const [isCompareDialogVisible, setIsCompareDialogVisible] = useState(false)

  const [activeSubscriptionId, setActiveSubscriptionId] = useState('')

  const [isBookingHistoryVisible, setIsBookingHistoryVisible] = useState(false)

  useEffect(() => {
    if (month && year) {
      setIsLoading(true)

      bookingsApi
        .otaBookings({
          month: monthNamesToNumbers[month],
          year,
          kind,
          parentChainCode: parentChain,
          city: cityToFilter,
          groupId: groupToFilter,
          state: stateToFilter,
          isOtaOnly
        })
        .then(res => {
          setHotels(res.conversions)
          setIsErred(false)
        })
        .catch(() => {
          setIsErred(true)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [
    month,
    year,
    parentChain,
    kind,
    cityToFilter,
    groupToFilter,
    stateToFilter,
    isOtaOnly
  ])

  function handleTitleClick(i) {
    const sortableIndexes = [0, 1, 2, 3, 4, 5, 6, 7]

    if (sortIndex === i) {
      setSortAscending(d => !d)
    }

    if (sortableIndexes.includes(i)) {
      setSortIndex(i)
    }
  }

  function sortResults(results) {
    const sortedData = results.sort((a, b) => {
      switch (sortIndex) {
        case 0:
          if (a.fullName === b.fullName) return 0

          return a?.fullName?.toUpperCase() > b?.fullName?.toUpperCase() ? 1 : -1

        case 1:
          return new Date(a.otaBooking?.agencyName) - new Date(b.otaBooking?.agencyName)

        case 2:
          return new Date(a.otaBooking?.bookingDate) - new Date(b.otaBooking?.bookingDate)

        case 3:
          return new Date(a.otaBooking?.checkIn) - new Date(b.otaBooking?.checkIn)

        case 4:
          return new Date(a.directBooking?.textedOn) - new Date(b.directBooking?.textedOn)

        case 5:
          return (
            new Date(a.directBooking?.bookingDate) -
            new Date(b.directBooking?.bookingDate)
          )

        case 6:
          return new Date(a.directBooking?.checkIn) - new Date(b.directBooking?.checkIn)

        case 7:
          return Number(a.directBooking?.totalRate) - Number(b.directBooking?.totalRate)

        default:
          return 0
      }
    })

    return sortAscending ? sortedData : [...sortedData].reverse()
  }

  if (isLoading) {
    return (
      <div className={styles.bookingsWrapper}>
        <div className={styles.bookingsHeader}>
          <h2 className={styles.bookingsTitle}>
            Bookings for {month}, {year}
          </h2>{' '}
        </div>
        <LoadingIndicator inline />
      </div>
    )
  }

  if (isErred) {
    return (
      <div className={styles.bookingsWrapper}>
        <div className={styles.bookingsHeader}>
          <h2 className={styles.bookingsTitle}>
            Bookings for {month}, {year}
          </h2>{' '}
        </div>
        <p>Something went wrong while getting the booking list</p>
      </div>
    )
  }

  return (
    <div className={styles.bookingsWrapper}>
      {isCompareDialogVisible && (
        <ReservationCompareDialog
          bookingId={activeBookingId}
          priorBookingId={activePriorBookingId}
          onClose={() => setIsCompareDialogVisible(false)}
          priorBookingTitle="OTA booking"
          bookingTitle="Direct booking"
        />
      )}
      {isBookingHistoryVisible && activeSubscriptionId && (
        <BookingHistory
          subscriberId={activeSubscriptionId}
          onClose={() => {
            setIsBookingHistoryVisible(false)
            setActiveSubscriptionId(null)
          }}
        />
      )}
      <div className={styles.bookingsHeader}>
        {month ? (
          <h2 className={styles.bookingsTitle}>
            Bookings for {month}, {year}
          </h2>
        ) : (
          <h2 className={styles.bookingsTitle}>Bookings list</h2>
        )}
        <input
          type="search"
          className={styles.searchInput}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search with guest name"
          disabled={!month}
        />
      </div>
      {month && hotels ? (
        <div className={styles.tableHolder}>
          <Table
            widths={[170, 120, 110, 110, 110, 110, 110, 100, 120]}
            sortIndex={sortIndex}
            sortDirection={sortAscending ? 'up' : 'down'}
            handleTitleCellClick={handleTitleClick}
          >
            {hotels.map(hotel => (
              <div key={hotel.subsidiaryId}>
                <p className={styles.hotelName}>{hotel.hotelName}</p>
                <TitleRow className={styles.titleRow}>
                  <TitleCell>Guest name</TitleCell>
                  <TitleCell>OTA name</TitleCell>
                  <TitleCell>OTA booking</TitleCell>
                  <TitleCell>OTA stay dates</TitleCell>
                  <TitleCell>Invite date</TitleCell>
                  <TitleCell>Direct booking</TitleCell>
                  <TitleCell>Stay dates</TitleCell>
                  <TitleCell>Total rate</TitleCell>
                  <TitleCell></TitleCell>
                </TitleRow>
                {sortResults(hotel.bookings)
                  .filter(b =>
                    b?.fullName
                      ? b?.fullName?.toLowerCase().includes(searchQuery.toLowerCase())
                      : true
                  )
                  .map(b => (
                    <Row className={styles.row}>
                      <Cell className={styles.fullNameRow}>
                        {b.subscriptionId ? (
                          <button
                            className={styles.fullNameButton}
                            onClick={() => {
                              setActiveSubscriptionId(b.subscriptionId)
                              setIsBookingHistoryVisible(true)
                            }}
                          >
                            {b.fullName}
                          </button>
                        ) : (
                          b.fullName
                        )}
                      </Cell>
                      <Cell>
                        <span
                          title={b.otaBooking?.agencyName}
                          style={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            maxWidth: '100px'
                          }}
                        >
                          {b.otaBooking?.agencyName}
                        </span>
                      </Cell>
                      <Cell>
                        {b.otaBooking?.bookingDate
                          ? moment(b.otaBooking?.bookingDate).format('MMM D, YYYY')
                          : '-'}
                      </Cell>
                      <Cell>
                        <span
                          title={`${moment(b.otaBooking?.checkIn).format(
                            'MMM D, YYYY'
                          )} - ${moment(b.otaBooking?.checkOut).format('MMM D, YYYY')}`}
                        >
                          {getDateRange(b.otaBooking?.checkIn, b.otaBooking?.checkOut)}
                        </span>
                      </Cell>
                      <Cell>
                        {moment(b.directBooking?.textedOn).format('MMM D, YYYY')}
                      </Cell>
                      <Cell>
                        {b.directBooking?.bookingDate
                          ? moment(b.directBooking?.bookingDate).format('MMM D, YYYY')
                          : '-'}
                      </Cell>
                      <Cell>
                        <span
                          title={`${moment(b.directBooking?.checkIn).format(
                            'MMM D, YYYY'
                          )} - ${moment(b.directBooking?.checkOut).format(
                            'MMM D, YYYY'
                          )}`}
                        >
                          {getDateRange(
                            b.directBooking?.checkIn,
                            b.directBooking?.checkOut
                          )}
                        </span>
                      </Cell>
                      <Cell>{formatPrice(b.directBooking?.totalRate)}</Cell>
                      <Cell>
                        <TerciarySmallButton
                          disabled={!(b.directBooking?.id && b.otaBooking?.id)}
                          onClick={() => {
                            setActiveBookingId(b.directBooking?.id)
                            setActivePriorBookingId(b.otaBooking?.id)
                            setIsCompareDialogVisible(true)
                          }}
                        >
                          Compare
                        </TerciarySmallButton>
                      </Cell>
                    </Row>
                  ))}
              </div>
            ))}
          </Table>
        </div>
      ) : (
        <div className={styles.selectMonthIconHolder}>
          <SelectBarChart className={styles.selectMonthIcon} />
          <p className={styles.selectMonthText}>
            Select a month from charts to see the bookings
          </p>
        </div>
      )}
    </div>
  )
}

export default OTABookingsList
